export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://192.168.0.2:6020";

const getSessionId = () => {
  let sid = localStorage.getItem('guest_session_id');
  if (!sid) {
    sid = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('guest_session_id', sid);
  }
  return sid;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
    'x-session-id': getSessionId()
  };
};

export const getProductImageUrl = (url) => {
  if (!url) return `${BACKEND_URL}/uploads/panel_144led_pro.jpg`;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // If an image URL contains an /uploads/ path from an old backend host/IP,
    // normalize it to use the current BACKEND_URL from env
    if (url.includes('/uploads/')) {
      const uploadPath = url.substring(url.indexOf('/uploads/'));
      return `${BACKEND_URL}${uploadPath}`;
    }
    return url;
  }
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${BACKEND_URL}${cleanPath}`;
};

export const fetchProductsFromBackend = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/products`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (data.success && Array.isArray(data.products) && data.products.length > 0) {
      return data.products.map(p => ({
        ...p,
        image_url: getProductImageUrl(p.image_url)
      }));
    }
    return null;
  } catch (error) {
    console.warn("Backend API fetch failed, falling back to cached brochure data:", error);
    return null;
  }
};

export const fetchProductByIdFromBackend = async (id) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/products/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (data.success && data.product) {
      const p = data.product;
      const formattedImg = getProductImageUrl(p.image_url);
      return {
        ...p,
        image_url: formattedImg,
        images: Array.isArray(p.gallery_images) && p.gallery_images.length > 0 
          ? p.gallery_images.map(img => getProductImageUrl(img.image_url || img))
          : [formattedImg]
      };
    }
    return null;
  } catch (error) {
    console.warn("Backend API product fetch failed:", error);
    return null;
  }
};

// Customer Auth APIs
export const loginCustomer = async (email, password) => {
  const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  return response.json();
};

export const registerCustomer = async (name, email, password) => {
  const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password, role: 'customer' })
  });
  return response.json();
};

export const verifySignupOTP = async (email, otp) => {
  const response = await fetch(`${BACKEND_URL}/api/auth/verify-signup-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp })
  });
  return response.json();
};

export const forgotCustomerPassword = async (email) => {
  const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  return response.json();
};

export const verifyCustomerOTP = async (email, otp) => {
  const response = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp })
  });
  return response.json();
};

export const resetCustomerPassword = async (email, otp, newPassword) => {
  const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp, newPassword })
  });
  return response.json();
};

// Customer Wishlist APIs
export const fetchWishlistFromBackend = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/wishlist`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.success ? data.wishlist : null;
  } catch (error) {
    console.warn("Fetch wishlist failed:", error);
    return null;
  }
};

export const toggleWishlistBackend = async (productId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/wishlist/toggle`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ productId })
    });
    return response.json();
  } catch (error) {
    console.warn("Toggle wishlist failed:", error);
    return { success: false, message: error.message };
  }
};

export const removeFromWishlistBackend = async (productId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/wishlist/${productId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return response.json();
  } catch (error) {
    console.warn("Remove wishlist failed:", error);
    return { success: false, message: error.message };
  }
};

// Customer Cart APIs
export const fetchCartFromBackend = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/cart`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return null;
    return response.json();
  } catch (error) {
    console.warn("Fetch cart failed:", error);
    return null;
  }
};

export const addToCartBackend = async (productId, quantity = 1, selectedAddons = []) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/cart/add`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ productId, quantity, selectedAddons })
    });
    return response.json();
  } catch (error) {
    console.warn("Add to cart failed:", error);
    return { success: false, message: error.message };
  }
};

export const updateCartQuantityBackend = async (cartItemId, quantity) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/cart/item/${cartItemId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ quantity })
    });
    return response.json();
  } catch (error) {
    console.warn("Update cart quantity failed:", error);
    return { success: false, message: error.message };
  }
};

export const removeFromCartBackend = async (cartItemId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/cart/item/${cartItemId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return response.json();
  } catch (error) {
    console.warn("Remove from cart failed:", error);
    return { success: false, message: error.message };
  }
};

export const clearCartBackend = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/cart/clear`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return response.json();
  } catch (error) {
    console.warn("Clear cart failed:", error);
    return { success: false, message: error.message };
  }
};

export const reserveCartStockBackend = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/cart/reserve`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  } catch (error) {
    console.warn("Reserve cart stock failed:", error);
    return { success: false, message: error.message };
  }
};

// Customer Saved Addresses APIs
export const fetchUserAddressesFromBackend = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/addresses`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.success ? data.addresses : [];
  } catch (error) {
    console.warn("Fetch addresses failed:", error);
    return [];
  }
};

export const addUserAddressBackend = async (addressData) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/addresses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(addressData)
    });
    return response.json();
  } catch (error) {
    console.warn("Add address failed:", error);
    return { success: false, message: error.message };
  }
};

// Customer Orders API
export const fetchOrdersFromBackend = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/orders`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return { success: false, orders: [] };
    return await response.json();
  } catch (error) {
    console.warn("Fetch orders failed:", error);
    return { success: false, orders: [] };
  }
};

export const createOrderBackend = async (orderData) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/orders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(orderData)
    });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, message: data.message || `Server returned status ${response.status}` };
    }
    return data;
  } catch (error) {
    console.warn("Create order failed:", error);
    return { success: false, message: error.message || "Network error. Could not place order." };
  }
};

// Customer Product Reviews APIs
export const fetchProductReviewsFromBackend = async (productId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/products/${productId}/reviews`);
    if (!response.ok) return null;
    const data = await response.json();
    return data.success ? data : null;
  } catch (error) {
    console.warn("Fetch reviews failed:", error);
    return null;
  }
};

export const submitProductReviewBackend = async (reviewData) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/reviews`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(reviewData)
    });
    return await response.json();
  } catch (error) {
    console.warn("Submit review failed:", error);
    return { success: false, message: error.message || "Network error. Could not submit review." };
  }
};

export const trackOrderBackend = async (order_number, email) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/orders/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_number, email })
    });
    return await response.json();
  } catch (error) {
    console.warn("Track order failed:", error);
    return { success: false, message: error.message || "Network error looking up order status." };
  }
};

export const payOrderBackend = async (orderId, paymentData) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/pay`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(paymentData)
    });
    return await response.json();
  } catch (error) {
    console.warn("Pay order failed:", error);
    return { success: false, message: error.message || "Network error submitting payment." };
  }
};

export const createStripeCheckoutSessionBackend = async (orderId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/checkout-session`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return await response.json();
  } catch (error) {
    console.warn("Create Stripe checkout session failed:", error);
    return { success: false, message: error.message || "Network error initializing payment gateway." };
  }
};

export const verifyStripeSessionBackend = async (sessionId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/orders/verify-session/${sessionId}`, {
      headers: getAuthHeaders()
    });
    return await response.json();
  } catch (error) {
    console.warn("Verify Stripe session failed:", error);
    return { success: false, message: error.message || "Network error verifying payment session." };
  }
};

